"use client";
import React from "react";
import Link from "next/link";
import Image from "next/image";
import { MapPin } from "lucide-react";

import {
  formatDays,
  redirectGmap,
  USconvertTimeRange,
} from "@/services/commonFunction";
import { eventsData } from "@/services/dummay";
import { EventData, Event } from "@/types/common";
import { cleanHtmlStylesExtreme } from "@/utils/utils";

import InfiniteScroll from "react-infinite-scroll-component";
import Loader from "@/components/Loader";
import GumletImage from "@/utils/GumletSrcSet";

const getOrdinalSuffix = (day: string) => {
  if (!day) return "";

  const num = parseInt(day);
  if (isNaN(num)) return "";

  if (num >= 11 && num <= 13) return "th";

  const lastDigit = num % 10;
  switch (lastDigit) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export default function EventsListing({
  data,
  hasMore,
  fetchMore,
}: {
  data: EventData;
  hasMore: boolean;
  fetchMore: () => void;
}) {
  // Use dummy data only if events is undefined or null, not if it's an empty array
  const events =
    data.events === undefined || data.events === null
      ? eventsData
      : data.events;
  const hasEvents = events && events.length > 0;

  return (
    <div
      id="scrollableDiv"
      className="max-w-7xl h-screen overflow-y-auto hide-scrollbar lg:w-4/5 mx-auto w-full px-0 md:px-4 lg:px-0 lg:pl-8 lg:border-l border-inactiveGray"
    >
      {hasEvents ? (
        <InfiniteScroll
          dataLength={data.events.length}
          next={fetchMore}
          hasMore={hasMore}
          scrollableTarget="scrollableDiv"
          loader={<Loader />}
          endMessage={
            <div className="text-center text-gray-500 py-8">
              <p>There are no more events scheduled for the year.</p>
            </div>
          }
          className="space-y-10 relative"
        >
          {events?.map((event: Event, index: number) => (
            <div
              key={index}
              className="border-b border-borderGray last:border-b-0 pb-4"
            >
              <h2 className="text-xs font-Inter font-semibold mb-8 uppercase">
                {event.tag}
              </h2>
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Date column */}
                <div className="lg:min-w-[220px] min-w-full lg:border-r border-borderGray lg:pr-4 flex flex-col justify-between order-3 lg:order-1">
                  <Link href={`/event/${event.slug}`} prefetch={false}>
                    <>
                      <div className="hidden lg:flex flex-wrap pt-3">
                        <h3 className="text-5xl font-medium">
                          {!event.endDate ||
                          event.startDate === event.endDate ? (
                            <>
                              {event.startDate}
                              <sup>{getOrdinalSuffix(event.startDate)}</sup>
                            </>
                          ) : (
                            <>
                              {event.startDate}
                              <sup>{getOrdinalSuffix(event.startDate)}</sup>-
                              {event.endDate}
                              <sup>{getOrdinalSuffix(event.endDate)}</sup>
                            </>
                          )}
                        </h3>
                      </div>
                      <p className="text-base my-2 hidden lg:block">
                        {event.month}{" "}
                        {event.days ? ` | (${formatDays(event.days)})` : ""}
                      </p>
                    </>
                    {/* mobile date */}
                    <div className="block lg:hidden font-medium text-xl mb-2">
                      {event.month} | (
                      <span>{formatDays(event.days || "")}</span>)
                      <span className="ml-2">
                        {event.startDate}
                        {getOrdinalSuffix(event.startDate)}
                        {event.startDate !== event.endDate && (
                          <>
                            -{event.endDate}
                            {getOrdinalSuffix(event.endDate || "")}
                          </>
                        )}
                      </span>
                    </div>
                    <p className="text-sm text-inkBlack font-inter mb-2 lg:mb-4">
                      {/* {convertTimeRange(event.time || "", event.startDate, event.endDate)} */}
                      {/* {MRconvertTimeRange(event?.time || "")} */}
                      {USconvertTimeRange(
                        event?.time || "",
                        event?.show_start_time,
                        event?.show_end_time
                      ) +
                        " " +
                        (event.timezone &&
                        USconvertTimeRange(
                          event.time || "",
                          event?.show_start_time,
                          event?.show_end_time
                        ).trim() !== ""
                          ? event.timezone
                          : "")}
                    </p>
                  </Link>

                  {event?.location && event?.event_type == "Offline" ? (
                    <Link
                      href={redirectGmap(event.location)}
                      className="flex items-center gap-2 mt-2 lg:mt-0"
                      prefetch={false}
                    >
                      <MapPin size={15} className="w-4 h-4 flex-shrink-0" />
                      <span className="lg:text-xs text-sm line-clamp-1">
                        {event.location}
                      </span>
                    </Link>
                  ) : (
                    <span className="lg:text-xs text-sm">
                      {event.event_type}
                    </span>
                  )}
                </div>

                {/* Event details column */}
                <Link
                  href={`/event/${event.slug}`}
                  className="w-full lg:px-2 lg:border-r border-borderGray flex flex-col justify-between order-2 lg:order-2"
                  prefetch={false}
                >
                  <div className="flex flex-col">
                    <div className="">
                      <span className="text-[32px] text-inkBlack font-medium mb-3 capitalize line-clamp-2 ">
                        {event.title}
                      </span>
                    </div>
                    <div className="">
                      <span
                        className="text-inkBlack/60 text-sm mb-6 font-inter line-clamp-4"
                        dangerouslySetInnerHTML={{
                          __html: cleanHtmlStylesExtreme(
                            event.description || ""
                          ),
                        }}
                      ></span>
                    </div>
                  </div>

                  <div className="flex items-center gap-x-3">
                    <div className="h-4 w-4 flex-shrink-0">
                      <Image
                        src="/assets/people.svg"
                        alt="People Icon"
                        width={16}
                        height={16}
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <span className="lg:text-xs text-sm line-clamp-1 text-inkBlack">
                        {event.audience || "Everyone"}
                      </span>
                    </div>
                  </div>
                </Link>

                {/* Image column */}
                <Link
                  href={`/event/${event.slug}`}
                  className="w-full order-1 lg:order-3"
                  prefetch={false}
                >
                  <div className="relative w-full aspect-video">
                    <GumletImage
                      imageUrl={
                        event?.thumbnail ||
                        event?.image
                      }
                      fallbackSrc="/assets/images/dummy.jpg"
                      alt={event.title}
                      fill
                      className="object-cover rounded"
                      loading="lazy"
                    />
                  </div>
                </Link>
              </div>
            </div>
          ))}
        </InfiniteScroll>
      ) : (
        <div className="text-center text-lg text-gray-500 py-12">
          No events available at this time.
        </div>
      )}
      {/* </div> */}
    </div>
  );
}
